import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Wine, Home as HomeIcon, Map, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { ROUTE_PATHS } from "@/lib/index";
import { HERO_DATA, SERVICES_DATA, COMPANY_INFO } from "@/data/index";
import { ServiceCard, FeatureCard } from "@/components/Cards";
import { Button } from "@/components/ui/button";

/**
 * @description Page d'accueil de YKE Multi_Services
 * © 2026 YKE Multi_Services - Votre partenaire de prestige
 */
export default function Home() {
  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.BUSINESS_TEAM_1} 
            alt="Prestige Business"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/20 to-background/90" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <span className="inline-block py-1 px-3 mb-6 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium tracking-wider uppercase">
              {COMPANY_INFO.slogan}
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-foreground tracking-tight leading-tight">
              {HERO_DATA.title}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto">
              {HERO_DATA.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 h-14 text-lg transition-all hover:scale-105">
                <Link to={ROUTE_PATHS.SERVICES}>
                  {HERO_DATA.primaryCTA}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 h-14 text-lg border-primary/30 hover:bg-primary/5 transition-all hover:scale-105">
                <Link to={ROUTE_PATHS.CONTACT}>
                  {HERO_DATA.secondaryCTA}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats/Quick Info Section */}
      <section className="py-12 bg-card border-y border-border/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Wine className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">Vins de Luxe</h3>
              <p className="text-muted-foreground">Importation exclusive</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <HomeIcon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">Résidences</h3>
              <p className="text-muted-foreground">Haut standing garanti</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Map className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">Lotissement</h3>
              <p className="text-muted-foreground">Sécurité foncière totale</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Nos Secteurs d'Expertise</h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Une approche multidisciplinaire pour répondre à tous vos besoins en Côte d'Ivoire.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES_DATA.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Trust Section */}
      <section className="py-24 bg-secondary/30 relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 relative">
              <div className="aspect-square rounded-2xl overflow-hidden border-8 border-background shadow-2xl">
                <img 
                  src={IMAGES.WINE_CHAMPAGNE_1} 
                  alt="Prestige Experience"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-8 rounded-2xl shadow-xl hidden md:block">
                <p className="text-4xl font-bold">SIPIM</p>
                <p className="text-sm uppercase tracking-widest opacity-80">Port-Bouët, Abidjan</p>
              </div>
            </div>
            
            <div className="lg:w-1/2 space-y-8">
              <div>
                <h2 className="text-4xl font-bold mb-6 leading-tight">
                  Pourquoi Choisir <br /> 
                  <span className="text-primary">YKE Multi_Services ?</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  {COMPANY_INFO.history}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <FeatureCard 
                  title="Sécurité Juridique"
                  description="Tous nos terrains sont vérifiés et disposent de titres fonciers sécurisés."
                  icon={<ShieldCheck className="w-6 h-6" />}
                />
                <FeatureCard 
                  title="Qualité Premium"
                  description="Une sélection de vins et de résidences qui répondent aux standards internationaux."
                  icon={<Wine className="w-6 h-6" />}
                />
              </div>

              <div className="pt-6">
                <Button asChild className="rounded-full">
                  <Link to={ROUTE_PATHS.ABOUT}>En savoir plus sur nous</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-foreground text-background rounded-3xl p-12 md:p-20 text-center relative overflow-hidden">
            {/* Background pattern decoration */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-primary/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                Prêt à concrétiser votre projet ?
              </h2>
              <p className="text-xl text-background/80 mb-12 max-w-2xl mx-auto">
                Que ce soit pour une dégustation, un séjour ou un investissement foncier, nous sommes à votre écoute.
              </p>
              
              <div className="flex flex-wrap justify-center gap-8">
                <a href={`tel:${COMPANY_INFO.phone1.replace(/\s/g, '')}`} className="flex items-center gap-3 hover:text-primary transition-colors">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-semibold">{COMPANY_INFO.phone1}</span>
                </a>
                <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-3 hover:text-primary transition-colors">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-semibold">{COMPANY_INFO.email}</span>
                </a>
              </div>

              <div className="mt-12">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 rounded-full text-lg">
                  <Link to={ROUTE_PATHS.CONTACT}>Prendre rendez-vous</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
